import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import AdminLayout from '../../components/feature/AdminLayout';
import {
  fetchAdminRelease,
  publishAdminRelease,
  unpublishAdminRelease,
  type PlatformRelease,
  type ChangeCategory,
} from '../../api/releases.api';

const CATEGORY_META: Record<ChangeCategory, { label: string; icon: string; headerColor: string; itemColor: string }> = {
  feature: {
    label: 'المميزات والخصائص الجديدة',
    icon: 'ri-sparkling-fill',
    headerColor: 'text-emerald-700 bg-emerald-50 border-emerald-200',
    itemColor: 'border-r-4 border-r-emerald-500 bg-emerald-50/20',
  },
  improvement: {
    label: 'التحسينات وتطوير الأداء',
    icon: 'ri-flashlight-line',
    headerColor: 'text-indigo-700 bg-indigo-50 border-indigo-200',
    itemColor: 'border-r-4 border-r-indigo-500 bg-indigo-50/20',
  },
  fix: {
    label: 'إصلاحات الأخطاء والاستقرار',
    icon: 'ri-bug-line',
    headerColor: 'text-amber-700 bg-amber-50 border-amber-200',
    itemColor: 'border-r-4 border-r-amber-500 bg-amber-50/20',
  },
  breaking: {
    label: 'تغييرات جوهرية وتنبيهات الاستخدام',
    icon: 'ri-alert-line',
    headerColor: 'text-rose-700 bg-rose-50 border-rose-200',
    itemColor: 'border-r-4 border-r-rose-500 bg-rose-50/20',
  },
  security: {
    label: 'التحديثات والتحسينات الأمنية',
    icon: 'ri-shield-keyhole-line',
    headerColor: 'text-cyan-700 bg-cyan-50 border-cyan-200',
    itemColor: 'border-r-4 border-r-cyan-500 bg-cyan-50/20',
  },
};

export default function ReleasePreviewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [release, setRelease] = useState<PlatformRelease | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  const loadRelease = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError('');

    const res = await fetchAdminRelease(Number(id));
    setLoading(false);

    if (!res.ok) {
      if (res.unauthorized) {
        navigate('/admin/login', { replace: true });
        return;
      }
      setError(res.message);
      return;
    }

    setRelease(res.release);
  }, [id, navigate]);

  useEffect(() => {
    void loadRelease();
  }, [loadRelease]);

  const handlePublish = async () => {
    if (!release) return;
    setPublishing(true);
    setError('');
    const res = await publishAdminRelease(release.id);
    setPublishing(false);

    if (!res.ok) {
      setError(res.message);
      return;
    }

    setSuccessMsg('تم نشر هذا الإصدار بنجاح! أصبح مرئياً للجمهور الآن.');
    setRelease(res.release);
  };

  const handleUnpublish = async () => {
    if (!release) return;
    if (!window.confirm('هل تريد إلغاء النشر وإعادة الإصدار كمسودة؟')) return;
    setPublishing(true);
    setError('');
    const res = await unpublishAdminRelease(release.id);
    setPublishing(false);

    if (!res.ok) {
      setError(res.message);
      return;
    }

    setSuccessMsg('تم إلغاء نشر الإصدار.');
    setRelease(res.release);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="bg-white p-12 rounded-2xl border border-gray-100 text-center text-gray-500 max-w-4xl mx-auto" dir="rtl">
          <i className="ri-loader-4-line text-3xl animate-spin text-blue-600 mb-2 inline-block" />
          <p>جاري تحميل معاينة الإصدار...</p>
        </div>
      </AdminLayout>
    );
  }

  if (error || !release) {
    return (
      <AdminLayout>
        <div className="bg-white p-8 rounded-2xl border border-gray-100 text-center max-w-4xl mx-auto" dir="rtl">
          <p className="text-rose-600 mb-4">{error || 'لم يتم العثور على الإصدار'}</p>
          <Link to="/releases" className="px-4 py-2 bg-gray-100 rounded-xl text-sm font-semibold">
            العودة لقائمة الإصدارات
          </Link>
        </div>
      </AdminLayout>
    );
  }

  // Group changes by category
  const groupedChanges: Partial<Record<ChangeCategory, typeof release.changes>> = {};
  (release.changes || []).forEach((c) => {
    if (!groupedChanges[c.category]) {
      groupedChanges[c.category] = [];
    }
    groupedChanges[c.category]!.push(c);
  });

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-4xl mx-auto pb-12" dir="rtl">
        {/* Top Control Bar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-5 rounded-2xl border border-gray-100 shadow-sm">
          <div className="flex items-center gap-3">
            <Link
              to="/releases"
              className="w-9 h-9 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 flex items-center justify-center transition-colors"
              title="العودة"
            >
              <i className="ri-arrow-right-line" />
            </Link>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold px-2 py-0.5 rounded bg-blue-100 text-blue-800">
                  معاينة مباشرة (Preview Mode)
                </span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                  release.status === 'published' ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-700'
                }`}>
                  الحالة: {release.status === 'published' ? 'منشور' : 'مسودة'}
                </span>
              </div>
              <p className="text-xs text-gray-400 mt-0.5">هذه المعاينة توضح شكل وتفاصيل الإصدار على الموقع الخارجي للجمهور</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Link
              to={`/releases/${release.id}/edit`}
              className="px-4 py-2 rounded-xl border border-gray-200 text-gray-700 hover:bg-gray-50 text-xs font-semibold flex items-center gap-1.5"
            >
              <i className="ri-edit-line" />
              تعديل
            </Link>

            {release.status !== 'published' ? (
              <button
                onClick={() => void handlePublish()}
                disabled={publishing}
                className="px-5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold flex items-center gap-1.5 shadow-sm shadow-emerald-200"
              >
                {publishing ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-upload-cloud-2-line" />}
                نشر هذا الإصدار الآن
              </button>
            ) : (
              <button
                onClick={() => void handleUnpublish()}
                disabled={publishing}
                className="px-4 py-2 rounded-xl bg-amber-50 hover:bg-amber-100 text-amber-700 text-xs font-semibold flex items-center gap-1.5"
              >
                <i className="ri-eye-off-line" />
                إلغاء النشر
              </button>
            )}
          </div>
        </div>

        {/* Feedback message */}
        {successMsg && (
          <div className="p-4 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-xl flex items-center gap-3">
            <i className="ri-checkbox-circle-line text-xl" />
            <span>{successMsg}</span>
          </div>
        )}

        {/* Public-facing Preview Card */}
        <div className="bg-white rounded-3xl border border-gray-200 shadow-xl overflow-hidden">
          {/* Header Banner */}
          <div className="bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-950 text-white p-8 md:p-10 relative">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
              <div className="flex items-center gap-2">
                <span className="px-3 py-1 rounded-full bg-white/10 backdrop-blur-md text-xs font-semibold text-blue-200 border border-white/10">
                  {release.product?.name}
                </span>
                <span className="px-2.5 py-0.5 rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium uppercase border border-blue-400/20">
                  {release.release_type}
                </span>
                {release.is_current && (
                  <span className="px-2.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 text-xs font-bold border border-emerald-400/30 flex items-center gap-1">
                    <i className="ri-check-double-line" />
                    الإصدار المعتمد الحالي
                  </span>
                )}
              </div>

              <div className="text-xs text-white/60">
                تاريخ الإصدار: {release.release_date}
              </div>
            </div>

            <div className="flex items-baseline gap-4 mb-3">
              <h1 className="text-3xl md:text-4xl font-black font-mono tracking-tight text-white">
                v{release.version}
              </h1>
              <h2 className="text-xl md:text-2xl font-bold text-blue-100">
                {release.title}
              </h2>
            </div>

            {release.summary && (
              <p className="text-base text-white/75 max-w-2xl leading-relaxed mt-2">
                {release.summary}
              </p>
            )}

            {/* Artifacts / Download button if desktop */}
            {release.artifacts && release.artifacts.length > 0 && (
              <div className="mt-6 pt-6 border-t border-white/10 flex flex-wrap items-center gap-4">
                {release.artifacts.map((art) => (
                  <div
                    key={art.id}
                    className="flex flex-col sm:flex-row sm:items-center gap-3 p-3.5 rounded-2xl bg-white/10 backdrop-blur-md border border-white/15"
                  >
                    <a
                      href={art.download_url || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-5 py-2.5 rounded-xl bg-blue-500 hover:bg-blue-400 text-white font-bold text-sm flex items-center gap-2 transition-all shadow-lg shadow-blue-500/30"
                    >
                      <i className="ri-download-2-fill text-lg" />
                      تنزيل برنامج التثبيت ({(art.file_size / (1024 * 1024)).toFixed(1)} MB)
                    </a>

                    <div className="text-xs space-y-0.5 text-white/80 font-mono">
                      <div>{art.original_name}</div>
                      {art.file_hash && (
                        <div className="text-[11px] text-white/50 truncate max-w-xs" title={art.file_hash}>
                          SHA-256: {art.file_hash.slice(0, 16)}...
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Body Content */}
          <div className="p-8 md:p-10 space-y-10">
            {/* Categorized Changes */}
            {release.changes && release.changes.length > 0 ? (
              <div className="space-y-8">
                {(['feature', 'improvement', 'fix', 'breaking', 'security'] as ChangeCategory[]).map((cat) => {
                  const items = groupedChanges[cat];
                  if (!items || items.length === 0) return null;
                  const catMeta = CATEGORY_META[cat];

                  return (
                    <div key={cat} className="space-y-4">
                      <div className={`flex items-center gap-2.5 px-4 py-2 rounded-xl border font-bold text-sm w-fit ${catMeta.headerColor}`}>
                        <i className={`${catMeta.icon} text-base`} />
                        <span>{catMeta.label} ({items.length})</span>
                      </div>

                      <div className="space-y-3">
                        {items.map((item, idx) => (
                          <div
                            key={idx}
                            className={`p-4 rounded-xl border border-gray-100 ${catMeta.itemColor} space-y-1`}
                          >
                            <div className="flex items-center gap-2 font-bold text-gray-900 text-base">
                              <span>{item.title}</span>
                              {item.badge && (
                                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-md bg-blue-100 text-blue-800">
                                  {item.badge}
                                </span>
                              )}
                            </div>
                            {item.description && (
                              <p className="text-sm text-gray-600 leading-relaxed">
                                {item.description}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="p-6 rounded-2xl bg-gray-50 text-center text-gray-500 text-sm">
                لا توجد تفاصيل مدرجة في سجل التغييرات لهذا الإصدار.
              </div>
            )}

            {/* System Requirements & Compatibility */}
            {(release.system_requirements || release.compatibility_notes || release.upgrade_instructions) && (
              <div className="pt-8 border-t border-gray-100 grid grid-cols-1 md:grid-cols-2 gap-6">
                {release.system_requirements && (
                  <div className="p-5 rounded-2xl bg-slate-50 border border-slate-200/80 space-y-2">
                    <div className="flex items-center gap-2 font-bold text-slate-800 text-sm">
                      <i className="ri-cpu-line text-blue-600" />
                      الحد الأدنى لمتطلبات التشغيل
                    </div>
                    <p className="text-xs text-slate-600 leading-relaxed whitespace-pre-line">
                      {release.system_requirements}
                    </p>
                  </div>
                )}

                {release.compatibility_notes && (
                  <div className="p-5 rounded-2xl bg-slate-50 border border-slate-200/80 space-y-2">
                    <div className="flex items-center gap-2 font-bold text-slate-800 text-sm">
                      <i className="ri-shield-check-line text-emerald-600" />
                      ملاحظات التوافق
                    </div>
                    <p className="text-xs text-slate-600 leading-relaxed whitespace-pre-line">
                      {release.compatibility_notes}
                    </p>
                  </div>
                )}

                {release.upgrade_instructions && (
                  <div className="md:col-span-2 p-5 rounded-2xl bg-amber-50/60 border border-amber-200 space-y-2">
                    <div className="flex items-center gap-2 font-bold text-amber-900 text-sm">
                      <i className="ri-file-list-3-line text-amber-600" />
                      تعليمات وإرشادات الترقية
                    </div>
                    <p className="text-xs text-amber-800 leading-relaxed whitespace-pre-line">
                      {release.upgrade_instructions}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
